/**
 * Resolve AR entry id from workbench row/entity context for collection contact open.
 * Does not infer from customerId — only explicit fields or ar_collection entity id.
 */

import type { UniversalBaseEntity } from '@shared/types/entity';

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readCustomField(record: Record<string, unknown>, key: string): string | null {
  const customFields = record.customFields;
  if (!customFields) {
    return null;
  }

  if (Array.isArray(customFields)) {
    for (const entry of customFields) {
      if (entry && typeof entry === 'object' && (entry as { key?: unknown }).key === key) {
        return readTrimmedString((entry as { value?: unknown }).value);
      }
    }
    return null;
  }

  if (typeof customFields === 'object') {
    return readTrimmedString((customFields as Record<string, unknown>)[key]);
  }

  return null;
}

/**
 * Returns arEntryId when the selected entity exposes it; otherwise null.
 */
export function resolveArEntryIdFromWorkbenchEntity(
  entity: UniversalBaseEntity | Record<string, unknown>
): string | null {
  const record = entity as Record<string, unknown>;

  const explicit =
    readTrimmedString(record.arEntryId) ??
    readTrimmedString(record.ar_entry_id) ??
    readCustomField(record, 'arEntryId') ??
    readCustomField(record, 'ar_entry_id') ??
    readCustomField(record, 'linkedArEntryId') ??
    readCustomField(record, 'linked_ar_entry_id') ??
    readCustomField(record, 'sourceArEntryId') ??
    readCustomField(record, 'source_ar_entry_id');

  if (explicit) {
    return explicit;
  }

  const businessDomain = readTrimmedString(record.businessDomain);
  if (businessDomain === 'ar_collection') {
    return readTrimmedString(record.id);
  }

  return null;
}
